import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FormTemplateQuestionFieldsKindEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import StrictModeDroppable from '@lib/components/StrictModeDroppable/StrictModeDroppable';
import getPrefilledQuestions from 'components/FormTemplateBuilder/utils/getPrefilledQuestions';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { useFieldArrayContext } from '@lib/components/ReactHookForm/FormProvider';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { formTemplateBuilderScrollboxId } from '@lib/enums/selectors';
import { PlanState } from '@lib/enums/plan';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { PrefilledQuestion } from '../types';
import styles from '../FormTemplateBuilderForm/FormTemplateBuilderForm.module.scss';

interface Props {
  fieldItem: PrefilledQuestion;
  index: number;
  isLocked?: boolean;
  onClick?: () => void;
  snapshot?: DraggableStateSnapshot;
  subProvided?: DraggableProvided;
}

function RenderSourceItem(props: Props) {
  const { t } = useTranslation();
  const { fieldItem, isLocked, onClick, subProvided, snapshot } = props;
  const { icon, renderCustomIcon } = fieldItem;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      onClick={onClick}
      className={classNames(styles.sourceItem, {
        [styles.sourceItemDragging]: snapshot?.isDragging,
        [styles.sourceItemDisabled]: isLocked,
      })}
    >
      <div
        {...subProvided?.dragHandleProps}
        className={classNames(styles.sourceItemGrid, {
          [styles.sourceItemGridLocked]: isLocked,
        })}
      >
        <span className={styles.sourceItemIcon}>
          {icon && <MaterialIcon icon={icon} symbolsOutlined />}
          {renderCustomIcon ? renderCustomIcon() : null}
        </span>
        <Typography variant="caption" className={styles.sourceItemName}>
          {t(fieldItem.nameTranslationKey)}
        </Typography>
        {isLocked && (
          <span className={styles.sourceItemLock}>
            <Tooltip
              body={t('unlock-document-question-tooltip')}
              placement="right"
            >
              <Chip
                type={ChipTypes.chipBlue}
                size={ChipSizes.small}
                leftIcon="lock"
                asBlockElement
              />
            </Tooltip>
          </span>
        )}
      </div>
    </div>
  );
}

function FormTemplateBuilderDefaultQuestions() {
  const { fieldArrayMethodsByName } = useFieldArrayContext();
  const { t } = useTranslation();
  const methods = fieldArrayMethodsByName.questions;
  const handleAppend = _get(methods, 'append', _noop);

  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  // For non-Full-Admin Users on a locked plan, drop the Document source
  // item entirely — the lock affordance is a Full-Admin-only upgrade CTA.
  const hideDocumentSource = isDocumentationLocked && !isFullAdmin;
  const prefilledQuestions = getPrefilledQuestions({ t }).filter(
    (fieldItem) =>
      !hideDocumentSource ||
      fieldItem.id !== FormTemplateQuestionFieldsKindEnum.Document,
  );

  return (
    <div className={styles.defaultQuestions}>
      <StrictModeDroppable
        droppableId="FieldArrayDroppable-questions-source"
        isDropDisabled
      >
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={styles.sourceItems}
          >
            {prefilledQuestions.map((fieldItem, index) => {
              const itemLocked =
                isDocumentationLocked &&
                fieldItem.id === FormTemplateQuestionFieldsKindEnum.Document;
              return (
                <Draggable
                  key={fieldItem.id}
                  draggableId={fieldItem.id}
                  index={index}
                  isDragDisabled={itemLocked}
                >
                  {(subProvided, snapshot) => (
                    <div
                      className={classNames(styles.sourceItemContainer, {
                        [styles.sourceItemSectionContainer]:
                          _get(
                            fieldItem,
                            `${FormTemplateFieldNames.Fields}.0.${FormTemplateFieldNames.Kind}`,
                          ) === FormTemplateQuestionFieldsKindEnum.Section,
                      })}
                    >
                      <div
                        className={styles.sourceItemWrap}
                        ref={subProvided.innerRef}
                        {...subProvided.draggableProps}
                        {...subProvided.dragHandleProps}
                        style={
                          snapshot.isDragging
                            ? subProvided.draggableProps.style
                            : undefined
                        }
                      >
                        <RenderSourceItem
                          fieldItem={fieldItem}
                          isLocked={itemLocked}
                          onClick={() => {
                            if (itemLocked) {
                              handleOpenPlanModal(PlanState.Locked, [
                                CompanyPlanUpgradeFeatureEnum.Documentation,
                              ]);
                              return;
                            }
                            handleAppend(fieldItem);
                            setTimeout(() => {
                              const scrollbox = document.getElementById(
                                formTemplateBuilderScrollboxId,
                              );
                              if (scrollbox) {
                                scrollbox.scrollTo({
                                  left: 0,
                                  top: scrollbox.scrollHeight,
                                  behavior: 'smooth',
                                });
                              }
                            }, 100);
                          }}
                          index={index}
                          snapshot={snapshot}
                          subProvided={subProvided}
                        />
                      </div>
                      {snapshot.isDragging && (
                        <div className={styles.sourceItemPlaceholder}>
                          <RenderSourceItem
                            fieldItem={fieldItem}
                            index={index}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              );
            })}
            <div className={styles.hidden}>{provided.placeholder}</div>
          </div>
        )}
      </StrictModeDroppable>
    </div>
  );
}

export default FormTemplateBuilderDefaultQuestions;
